import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
    CalendarDaysIcon, 
    ClockIcon, 
    VideoCameraIcon, 
    UserIcon,
    BellAlertIcon,
    AcademicCapIcon,
    CodeBracketIcon
} from '@heroicons/react/24/outline';
import { db, auth } from '../../firebase'; 
import { collection, query, orderBy, limit, onSnapshot, getDoc, doc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';

const StudentSchedule = () => {
    const navigate = useNavigate();
    const [scheduleItems, setScheduleItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [userData, setUserData] = useState(null);

    // ✅ دالة التنظيف الصارمة - جعلناها هنا لتكون متاحة في كل مكان داخل الكومبوننت لضمان نجاح الـ Build
    const clean = (str) => (str || "").toString().trim().replace(/[أإآ]/g, "ا").toLowerCase();

    useEffect(() => {
        const fetchSchedule = async () => {
            if (!auth.currentUser) return;

            try {
                const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
                const uData = userDoc.data();
                setUserData(uData);

                const lessonsRef = collection(db, "lessons");
                const quizzesRef = collection(db, "quizzes");

                const unsubscribeLessons = onSnapshot(query(lessonsRef, orderBy("createdAt", "desc"), limit(20)), (snapshot) => {
                    const lessons = snapshot.docs.map(doc => ({
                        id: doc.id,
                        ...doc.data(),
                        type: 'محاضرة مسجلة',
                        category: 'video'
                    }));

                    onSnapshot(query(quizzesRef, orderBy("createdAt", "desc"), limit(20)), (quizSnapshot) => {
                        const quizzes = quizSnapshot.docs.map(doc => ({
                            id: doc.id,
                            ...doc.data(),
                            type: 'اختبار تقييمي',
                            category: 'quiz'
                        }));

                        const allData = [...lessons, ...quizzes];

                        // 🔒 نظام الفلترة الحديدي (Strict Access Control)
                        const filteredData = allData.filter(item => {
                            const sCourse = uData?.activeCourse || "";
                            const sSubType = uData?.subscriptionType || "";
                            
                            const sCourseClean = clean(sCourse);
                            const sSubTypeClean = clean(sSubType);
                            const itemCourseClean = clean(item.courseType);

                            // 🛡️ [AL-BARON_FIX]: التحقق الذكي من تفعيل الكورسين معاً من قاعدة البيانات
                            const hasBothCourses = uData?.isCourse1Active === true && uData?.isCourse2Active === true;

                            // 1. المنهج الكامل (فتح كلي)
                            if (hasBothCourses || sCourse === "كورسين (كامل)" || sSubType === "كورسين (كامل)" || sCourseClean.includes("كامل") || sCourseClean.includes("المنهج كامل")) {
                                return true; 
                            }

                            // 2. المحتوى العام
                            if (itemCourseClean === "عام" || itemCourseClean === "محتوى عام") return true;

                            // 3. 🎯 منطق الكورس الأول الصارم (تم التحديث لدعم بوابات الفايربيس)
                            if (uData?.isCourse1Active === true || sCourseClean.includes("اول") || sSubTypeClean.includes("اول") || sCourseClean.includes("1")) {
                                if (itemCourseClean === "كورس اول" || itemCourseClean === "كورس 1" || itemCourseClean.includes("اول")) return true;
                            }

                            // 4. 🎯 منطق الكورس الثاني الصارم (تم التحديث لدعم بوابات الفايربيس)
                            if (uData?.isCourse2Active === true || sCourseClean.includes("ثاني") || sSubTypeClean.includes("ثاني") || sCourseClean.includes("2")) {
                                if (itemCourseClean === "كورس ثاني" || itemCourseClean === "كورس 2" || itemCourseClean.includes("ثاني")) return true;
                            }

                            return false; 
                        });

                        const sortedData = filteredData.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
                        setScheduleItems(sortedData);
                        setLoading(false);
                    });
                });
            } catch (error) {
                console.error("Schedule error:", error);
                setLoading(false);
            }
        };

        fetchSchedule();
    }, [userData?.uid]);

    // 🛡️ [AL-BARON_FIX]: تحديد اسم الاشتراك الديناميكي لعرضه بالواجهة بناءً على حالة البوابات المنطقية
    const activeSubscriptionName = (userData?.isCourse1Active === true && userData?.isCourse2Active === true) 
        ? "المنهج كامل" 
        : (userData?.activeCourse || userData?.subscriptionType || "بانتظار التفعيل");

    if (loading) return (
        <div className="min-h-[400px] flex items-center justify-center bg-transparent">
            <div className="w-8 h-8 border-4 border-[#007AFF] border-t-transparent rounded-full animate-spin"></div>
        </div>
    );

    return (
        <div style={{ fontFamily: "'Cairo', sans-serif" }} className="max-w-4xl mx-auto space-y-6 pb-10 text-right" dir="rtl">
            
            <div className="flex justify-between items-center px-4">
                <div>
                    <h2 className="text-2xl font-black text-gray-800 dark:text-white">جدول التحديثات المباشرة</h2>
                    <p className="text-sm text-gray-400 font-bold">اشتراكك الحالي: {activeSubscriptionName}</p>
                </div>
                <div className="ios-glass p-3 rounded-2xl border border-white/40 shadow-sm bg-white/20 backdrop-blur-md">
                    <CalendarDaysIcon className="w-6 h-6 text-[#007AFF]" />
                </div>
            </div>

            <div className="space-y-4 px-2">
                {scheduleItems.length > 0 ? scheduleItems.map((item, index) => (
                    <motion.div 
                        key={item.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="ios-glass p-6 rounded-[30px] border border-white/30 dark:border-white/10 bg-white/20 dark:bg-white/5 backdrop-blur-xl shadow-sm hover:shadow-md transition-all flex flex-col md:flex-row gap-6 items-center"
                    >
                        <div className="flex flex-col items-center justify-center bg-white dark:bg-white/10 rounded-2xl p-4 min-w-[100px] shadow-sm border border-white/20">
                            <span className="text-[10px] font-black text-gray-400 uppercase tracking-tighter">إضافة جديدة</span>
                            <span className="text-xl font-black text-[#007AFF] dark:text-white">
                                {item.createdAt?.toDate ? item.createdAt.toDate().toLocaleDateString('ar-EG', { day: 'numeric' }) : '--'}
                            </span>
                            <span className="text-[10px] font-bold text-gray-500">
                                {item.createdAt?.toDate ? item.createdAt.toDate().toLocaleDateString('ar-EG', { month: 'short' }) : '--'}
                            </span>
                        </div>

                        <div className="flex-1 text-center md:text-right space-y-1">
                            <div className="flex flex-wrap justify-center md:justify-start gap-2 mb-2">
                                <span className={`text-[9px] font-black px-3 py-1 rounded-full uppercase ${
                                    item.category === 'quiz' ? 'bg-orange-100 text-orange-600' : 'bg-blue-100 text-[#007AFF]'
                                }`}>
                                    {item.type}
                                </span>
                                <span className="bg-green-100 text-green-600 text-[9px] font-black px-3 py-1 rounded-full uppercase">
                                    متاح لاشتراكك
                                </span>
                            </div>
                            <h3 className="text-lg font-black text-gray-800 dark:text-white">{item.title}</h3>
                            <div className="flex items-center justify-center md:justify-start gap-4 text-xs text-gray-500 dark:text-gray-400 font-bold">
                                <span className="flex items-center gap-1 font-black text-[#007AFF]"><ClockIcon className="w-4 h-4" /> {item.courseType}</span>
                                <span className="flex items-center gap-1"><UserIcon className="w-4 h-4" /> الأستاذة نور عماد</span>
                            </div>
                        </div>

                        <button 
                            onClick={() => {
                                if (item.category === 'quiz') {
                                    navigate('/student/quizzes');
                                } else {
                                    const cTypeClean = clean(item.courseType);
                                    const coursePath = cTypeClean.includes("ثاني") || cTypeClean.includes("2") ? "course2" : "course1";
                                    navigate(`/student/course/${coursePath}`);
                                }
                            }}
                            className="px-8 py-3 rounded-2xl bg-[#007AFF] text-white font-black text-sm shadow-lg shadow-blue-500/20 active:scale-95 transition-all"
                        >
                            {item.category === 'quiz' ? 'ابدأ الاختبار' : 'مشاهدة المحاضرة'}
                        </button>
                    </motion.div>
                )) : (
                    <div className="py-20 text-center text-gray-400 font-bold ios-glass rounded-3xl bg-white/10">لا توجد دروس جديدة متاحة لاشتراكك حالياً.</div>
                )}
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/10 p-6 rounded-[30px] border border-blue-100 dark:border-blue-900/20 flex items-start gap-4 mx-2">
                <BellAlertIcon className="w-6 h-6 text-[#007AFF] shrink-0" />
                <p className="text-xs text-blue-700 dark:text-blue-400 font-bold leading-relaxed">
                    نظام الفلترة مفعل: محاضرات ( {activeSubscriptionName} ) تظهر لك حصراً.
                </p>
            </div>

            {/* 🛡️ قسم الحقوق وقسم التطوير - AL-BARON (خط أحمر وممنوع الحذف) */}
            <footer className="pt-10 flex flex-col items-center opacity-70 pb-10 space-y-3">
                <div className="flex items-center gap-2 grayscale dark:invert transition-all hover:grayscale-0">
                    <CodeBracketIcon className="w-5 h-5 text-[#007AFF]" />
                    <span className="text-[11px] font-black tracking-[0.2em] leading-none uppercase text-gray-600 dark:text-white">
                        AL-BARON DEVELOPMENT DEPARTMENT
                    </span>
                </div>
                <div className="flex flex-col items-center space-y-1">
                    <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400 tracking-wider">
                        ALL RIGHTS RESERVED © 2026
                    </span>
                    <div className="h-[1px] w-12 bg-gray-300 dark:bg-gray-700 my-1"></div>
                    <span className="text-[12px] font-black text-[#007AFF] tracking-[0.3em] uppercase">
                        MK@BARON
                    </span>
                    <span className="text-[8px] font-black text-gray-400 tracking-widest mt-1">
                        PLATFORM VERSION 2.0.26
                    </span>
                </div>
            </footer>
        </div>
    );
};

export default StudentSchedule;